import { isNil, uniq } from 'lodash-es';

import { useMinMaxXAxis } from './useMinMaxXAxis.js';
import { useXAxisConfig } from './useXAxisConfig.js';
import { useYAxisConfig } from './useYAxisConfig.js';
import type { ChartUnit } from '../../core/types/chart-unit.js';
import {
  TimeseriesDatapoint,
  TimeseriesUnit,
} from '../../core/types/timeseries.js';
import { getDatapointIsGap } from '../../core/utils/get-data-point-is-gap.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  CumulativePoint,
  getCumulativeValue,
} from '../components/displays/shared/utils/datapoint-stacking.js';
import {
  TimeseriesBandDataPoint,
  TimeseriesBandSlotProps,
} from '../types/timeseries-band.js';
import {
  DatapointsMap,
  TimeseriesPerVariant,
  TimeseriesVariantSlotProps,
  ValueRepresentationPerVariant,
  WeakMapDatapoints,
  WeakMapSeries,
} from '../types/timeseries.js';
import { getThresholdValue } from '../utils/apply-gap-policy.js';
import { getDatapointValue } from '../utils/get-datapoint-value.js';
import { getRelativeConf } from '../utils/get-relative-conf.js';
import { getSanitizedValueRepresentation } from '../utils/get-sanitized-value-representation.js';
import { isStacked } from '../utils/is-stacked.js';
import { getDatapointTime } from '../utils/timeseries.js';

export function useBuildFilteredMetadata(
  data: TimeseriesPerVariant,
  valueRepPerVariant: ValueRepresentationPerVariant,
  seriesMap: WeakMapSeries,
  units: ChartUnit[],
) {
  const xAxisConfig = useXAxisConfig();
  const yAxisConfig = useYAxisConfig();
  const { min, max } = useMinMaxXAxis();

  const datapointsMap: WeakMapDatapoints = new WeakMap();

  const sanitizedValueRepPerVariant = getSanitizedValueRepresentation(
    data,
    valueRepPerVariant,
  );

  const filteredTimeseries: TimeseriesPerVariant = { ...data };
  const filteredUnits: TimeseriesUnit[] = [];
  const originalFilteredUnits: TimeseriesUnit[] = [];
  let relativeGroupedValues;

  for (const variant of data.shapePriority) {
    const cumulativeStackedPoints = new Map<string, CumulativePoint>();
    const lastSignStacked = new Map<string, number>();

    const isBand = variant === 'band';

    const variantSeries = data[variant];

    relativeGroupedValues = getRelativeConf(
      variantSeries,
      variant,
      sanitizedValueRepPerVariant,
      !isStacked(variant) ? relativeGroupedValues : undefined,
    );

    const newSeries: (TimeseriesVariantSlotProps | TimeseriesBandSlotProps)[] =
      [];

    for (let i = 0; i < variantSeries.length; i++) {
      const series = variantSeries[i];
      const seriesMetadata = safeGet(seriesMap, series);
      const { id, color, gapPolicy, pointsDisplay } = seriesMetadata;
      const unit = relativeGroupedValues ? '%' : series.unit;
      const originalUnit = series.unit;

      const metadata = {
        id,
        color,
        unit,
        originalUnit,
        gapPolicy,
        pointsDisplay,
      };

      // FYI: this will respect the left right unit order [leftUnit, rightUnit]
      if (units.length === 1 && yAxisConfig.right && !yAxisConfig.left) {
        filteredUnits.push(undefined);
      }

      filteredUnits.push(unit);
      originalFilteredUnits.push(originalUnit);

      seriesMap.set(series, metadata);

      const thresholdGap = getThresholdValue(gapPolicy);
      const newDatapoints: (TimeseriesDatapoint | TimeseriesBandDataPoint)[] =
        [];

      // FYI: DO NOT use SPREAD OPERATOR, this will significantly impact the performance of this code.
      for (let j = 0; j < series.datapoints.length; j++) {
        const datapoint = series.datapoints[j];

        // Validate if the data point is within the min/max range
        if (xAxisConfig && (xAxisConfig.min || xAxisConfig.max)) {
          const dataPointTimestamp = getDatapointTime(
            datapoint.start,
            datapoint.end,
            (datapoint as TimeseriesDatapoint).center,
          );
          const dataPointOutOfBounds =
            dataPointTimestamp < +min || dataPointTimestamp > +max;
          if (dataPointOutOfBounds) {
            continue;
          }
        }

        const nextDatapoint = series.datapoints[j + 1];

        const { absolute, value, y0, absoluteY0, y1, absoluteY1 } =
          getDatapointValue(
            datapoint,
            variant,
            originalUnit,
            relativeGroupedValues,
          );

        const isBandPointUndefined = isBand && (isNil(y0) || isNil(y1));
        const isPointUndefined = !isBand && isNil(value);

        // FYI: this is the undefined validation, do not use a point if its undefined.
        if (isBandPointUndefined || isPointUndefined) {
          continue;
        }

        const isGap =
          gapPolicy !== 'connect' &&
          getDatapointIsGap(datapoint, nextDatapoint, thresholdGap);

        let stackedSign = value ?? 0;
        const key =
          datapoint.start.getTime().toString() + datapoint.end?.getTime();

        if (value === 0) {
          const prevSign = lastSignStacked.get(key) ?? 0;

          stackedSign = prevSign;
        }

        const cumulativeStack = isStacked(variant)
          ? getCumulativeValue(
              cumulativeStackedPoints,
              datapoint as TimeseriesDatapoint,
              relativeGroupedValues ? originalUnit : unit,
              id,
              value,
              stackedSign,
            )
          : undefined;

        const datapointMetadata: DatapointsMap = {
          start: datapoint.start,
          end: datapoint.end,
          center: (datapoint as TimeseriesDatapoint).center,
          seriesId: id,
          color,
          unit,
          isGap: false,
          absoluteValue: absolute,
          value,
          variant,
          y0: y0 as number,
          y1: y1 as number,
          absoluteY0,
          absoluteY1,
          cumulativeValue: cumulativeStack?.cumulativeValue ?? 0,
          seriesName: series.name,
          originalUnit: originalUnit,
        };

        if (isStacked(variant) && cumulativeStack) {
          cumulativeStackedPoints.set(cumulativeStack.key, {
            seriesId: id,
            datapointsCumulative: cumulativeStack.datapointsCumulative,
            baseCumulative: cumulativeStack.baseCumulative,
          });

          lastSignStacked.set(key, stackedSign);
        }

        datapointsMap.set(datapoint, datapointMetadata);
        newDatapoints.push(datapoint);

        if (isGap) {
          const metadataGappedDatapoint = {
            ...datapointMetadata,
            isGap: true,
            value: Infinity,
          };
          const gappedDatapoint = {
            start: datapoint.end ?? datapoint.start,
            end: nextDatapoint.start,
            isGap: true,
            value: Infinity,
          };

          datapointsMap.set(gappedDatapoint, metadataGappedDatapoint);
          newDatapoints.push(gappedDatapoint);
        }
      }

      const { datapoints, ...spreadSeries } = series;
      const newSingleSeries = { ...spreadSeries, datapoints: newDatapoints } as
        | TimeseriesBandSlotProps
        | TimeseriesVariantSlotProps;

      newSeries.push(newSingleSeries);
      seriesMap.set(newSingleSeries, metadata);
    }

    filteredTimeseries[variant] = newSeries as TimeseriesVariantSlotProps[] &
      TimeseriesBandSlotProps[];
  }

  return {
    seriesMap: seriesMap,
    datapointsMap,
    filteredUnits: uniq(filteredUnits),
    filteredTimeseries,
    originalFilteredUnits: uniq(originalFilteredUnits),
  };
}
